/**
 * PDF Page Break Helper
 * Utility functions for managing page breaks in PDF generation
 */

// A4 dimensions in pixels (based on puppeteer's exact A4 specifications)
export const A4_DIMENSIONS = {
  // 8.2677in × 11.6929in at 96 DPI
  WIDTH: 794, // 8.2677in * 96dpi ≈ 794px
  HEIGHT: 1123, // 11.6929in * 96dpi ≈ 1123px
  MARGIN_TOP: 40,
  MARGIN_BOTTOM: 40,
  MARGIN_LEFT: 40,
  MARGIN_RIGHT: 40
};

/**
 * Calculate the content height that can fit on a page
 * @returns {number} Available height in pixels
 */
export const getAvailablePageHeight = () => {
  return A4_DIMENSIONS.HEIGHT - A4_DIMENSIONS.MARGIN_TOP - A4_DIMENSIONS.MARGIN_BOTTOM;
};

/**
 * Calculate the content width that can fit on a page
 * @returns {number} Available width in pixels
 */
export const getAvailablePageWidth = () => {
  return A4_DIMENSIONS.WIDTH - A4_DIMENSIONS.MARGIN_LEFT - A4_DIMENSIONS.MARGIN_RIGHT;
};

/**
 * Split content based on page breaks for puppeteer PDF generation
 * @param {Array} content - Array of React elements or content objects
 * @param {Function} getElementHeight - Function to estimate element height
 * @returns {Array} Array of pages with elements
 */
export const splitContentIntoPages = (content, getElementHeight) => {
  const availableHeight = getAvailablePageHeight();
  const pages = [];
  let currentPage = [];
  let currentHeight = 0;
  
  content.forEach(element => {
    const elementHeight = getElementHeight(element);
    
    // Check if the element would cause page overflow
    if (currentHeight + elementHeight > availableHeight) {
      // Element doesn't fit on current page
      pages.push(currentPage);
      currentPage = [element];
      currentHeight = elementHeight;
    } else {
      // Element fits on current page
      currentPage.push(element);
      currentHeight += elementHeight;
    }
  });
  
  // Add the last page if it has content
  if (currentPage.length > 0) {
    pages.push(currentPage);
  }
  
  return pages;
};

/**
 * Add CSS styles to handle page breaks correctly in puppeteer
 * @param {Document} document - The document object
 */
export const addPageBreakStyles = (document) => {
  const style = document.createElement('style');
  style.textContent = `
    @page {
      size: A4;
      margin: 0;
    }
    
    .pdf-page {
      page-break-after: always;
      page-break-inside: avoid;
    }
    
    /* Last page doesn't need a page break after */
    .pdf-page:last-child {
      page-break-after: auto;
    }
  `;
  document.head.appendChild(style);
}; 